import React, { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Separator } from "./ui/separator";
import { Loader2, Settings, Save, Home, User } from "lucide-react";
import { Link } from "react-router-dom";

const DashboardDemo = () => {
  const [settings, setSettings] = useState({
    goldSpot: "3300.00",
    platinumSpot: "1398.00",
    silverSpot: "38.00",
    goldsmithWage: "75.00",
    retailMultiplier: "2.5",
    laserPreference: "Laser",
    storeName: "Golden Craft Jewelry",
    userName: "John Doe"
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    // Simulate save
    setTimeout(() => {
      setIsSaving(false);
      alert("Settings saved successfully! (This is a demo)");
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
              <Home className="h-5 w-5" />
              <span>Back to Quotes</span>
            </Link>
            <Separator orientation="vertical" className="h-6" />
            <div className="flex items-center gap-2">
              <Settings className="h-6 w-6" />
              <div>
                <h1 className="text-2xl font-bold text-slate-900">StudioIQ Settings</h1>
                <p className="text-sm text-slate-600">demo@studioiq.co</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 bg-blue-50 px-3 py-1 rounded-full">
              <User className="h-4 w-4 text-blue-600" />
              <span className="text-sm text-blue-600">Demo User</span>
            </div>
          </div>
        </div>

        {/* Demo Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span className="text-blue-700 font-medium">Demo Mode</span>
          </div>
          <p className="text-blue-600 text-sm mt-1">
            This is what the full settings dashboard looks like after Clerk authentication. 
            Sign in to access the real settings that save to Cloudflare KV.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Metal Prices */}
          <Card>
            <CardHeader>
              <CardTitle>Metal Spot Prices (per oz)</CardTitle>
              <CardDescription>
                Current market prices for precious metals used in jewelry pricing calculations
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="goldSpot">Gold ($)</Label>
                <Input
                  id="goldSpot"
                  type="number"
                  step="0.01"
                  placeholder="3300.00"
                  value={settings.goldSpot}
                  onChange={(e) => handleInputChange('goldSpot', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="platinumSpot">Platinum ($)</Label>
                <Input
                  id="platinumSpot"
                  type="number"
                  step="0.01"
                  placeholder="1398.00"
                  value={settings.platinumSpot}
                  onChange={(e) => handleInputChange('platinumSpot', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="silverSpot">Silver ($)</Label>
                <Input
                  id="silverSpot"
                  type="number"
                  step="0.01"
                  placeholder="38.00"
                  value={settings.silverSpot}
                  onChange={(e) => handleInputChange('silverSpot', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Labor & Markup */}
          <Card>
            <CardHeader>
              <CardTitle>Labor & Markup Settings</CardTitle>
              <CardDescription>
                Configure labor costs and retail pricing multipliers
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="goldsmithWage">Labor Wage ($/hour)</Label>
                <Input
                  id="goldsmithWage"
                  type="number"
                  step="0.01"
                  placeholder="75.00"
                  value={settings.goldsmithWage}
                  onChange={(e) => handleInputChange('goldsmithWage', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="retailMultiplier">Retail Mark-up (multiplier)</Label>
                <Input
                  id="retailMultiplier"
                  type="number"
                  step="0.01"
                  placeholder="2.5"
                  value={settings.retailMultiplier}
                  onChange={(e) => handleInputChange('retailMultiplier', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Laser Preference */}
          <Card>
            <CardHeader>
              <CardTitle>Laser Preference</CardTitle>
              <CardDescription>
                Choose your preferred welding/repair method
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={settings.laserPreference}
                onValueChange={(value) => handleInputChange('laserPreference', value)}
                className="flex flex-col space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Torch" id="torch" />
                  <Label htmlFor="torch">Torch</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Laser" id="laser" />
                  <Label htmlFor="laser">Laser (1.5x Mark-up)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Prompt Me" id="prompt" />
                  <Label htmlFor="prompt">Prompt Me</Label>
                </div>
              </RadioGroup>
            </CardContent>
          </Card>

          {/* User Info */}
          <Card>
            <CardHeader>
              <CardTitle>User Information</CardTitle>
              <CardDescription>
                Optional information for personalizing quotes and invoices
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="storeName">Store Name</Label>
                <Input
                  id="storeName"
                  placeholder="Your Jewelry Store"
                  value={settings.storeName}
                  onChange={(e) => handleInputChange('storeName', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="userName">User Name</Label>
                <Input
                  id="userName"
                  placeholder="John Doe"
                  value={settings.userName}
                  onChange={(e) => handleInputChange('userName', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-end">
            <Button 
              type="submit" 
              disabled={isSaving}
              className="bg-slate-900 hover:bg-slate-800"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Settings (Demo)
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DashboardDemo;